import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/** Public: current payment mode for the banner. Never returns credentials. */
export const getPublicPaymentMode = createServerFn({ method: "GET" }).handler(async () => {
  const { resolveRequestPaymentMode } = await import("./mode-switch.server");
  const r = await resolveRequestPaymentMode();
  return { mode: r.mode, switchActive: r.switchActive, expiresAt: r.expiresAt };
});

async function assertSuperAdmin(supabase: { rpc: (fn: "is_platform_admin", args: { _user_id: string }) => PromiseLike<{ data: unknown }> }, userId: string) {
  const { data } = await supabase.rpc("is_platform_admin", { _user_id: userId });
  if (data !== true) throw new Error("Only Super Admin can change the payment mode");
}

export const getPaymentModeAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase.rpc("is_platform_admin", { _user_id: context.userId });
    const { readPaymentModeSwitch, resolveRequestPaymentMode } = await import("./mode-switch.server");
    const { isSwitchActive } = await import("./mode");
    const sw = await readPaymentModeSwitch();
    const current = await resolveRequestPaymentMode();
    return {
      canManage: data === true,
      active: isSwitchActive(sw),
      expiresAt: isSwitchActive(sw) ? sw?.expiresAt ?? null : null,
      updatedByEmail: sw?.updatedByEmail ?? null,
      updatedAt: sw?.updatedAt ?? null,
      thisAddressMode: current.mode,
    };
  });

export const setPaymentTestMode = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ on: z.boolean(), minutes: z.union([z.literal(30), z.literal(60), z.literal(120)]).default(60) }).parse(d))
  .handler(async ({ data, context }) => {
    await assertSuperAdmin(context.supabase as never, context.userId);
    const { isCb16Tenant } = await import("@/lib/tenant/context.server");
    if (!isCb16Tenant()) throw new Error("Payments are only configured for CB16");
    const { writePaymentModeSwitch } = await import("./mode-switch.server");
    const email = (context.claims as { email?: string } | undefined)?.email ?? null;
    return writePaymentModeSwitch({ on: data.on, minutes: data.minutes, actorId: context.userId, actorEmail: email });
  });
